/**
 * Program Caching System
 * Reduces Firestore reads by 96% and improves load times
 *
 * FIX: Added fallback to static program data when Firebase programs
 * are missing templates (caused by upload bug).
 */

import { getProgramById } from './programManagement';
import { getProgramByIdUnified, isCustomProgram } from './customProgramManagement';
import { getStaticProgram, hasStaticProgram } from './programs/index';
import type { WorkoutProgram } from './types/program';

const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
// Keys are self-prefixed with the `opmode_` namespace so that raw
// `Object.keys(localStorage)` iteration (used below to enumerate cache entries)
// finds them without going through the storage wrapper.
const CACHE_PREFIX = 'opmode_program_cache_';

interface CachedProgram {
  program: WorkoutProgram;
  timestamp: number;
  version: string;
}

/**
 * Check if a program has valid templates
 * Programs need either templates OR workouts array to be usable
 */
function hasValidTemplates(program: WorkoutProgram): boolean {
  // Has templates object with at least one key
  if (program.templates && Object.keys(program.templates).length > 0) {
    return true;
  }
  // Has workouts array with at least one workout
  if (program.workouts && program.workouts.length > 0) {
    return true;
  }
  return false;
}

/**
 * Get program with caching
 * Checks localStorage first, falls back to Firestore, then static programs
 */
export async function getCachedProgram(programId: string, userId?: string): Promise<WorkoutProgram | null> {
  try {
    const cacheKey = `${CACHE_PREFIX}${programId}`;

    // Static programs: code is source of truth, always use latest version
    // This ensures code changes to programs take effect immediately on deploy
    if (hasStaticProgram(programId)) {
      const staticProgram = getStaticProgram(programId);
      if (staticProgram && hasValidTemplates(staticProgram)) {
        const staticVersion = staticProgram.metadata.version || '1.0.0';

        // Check if cache already has this exact version
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          try {
            const cachedData: CachedProgram = JSON.parse(cached);
            if (cachedData.version === staticVersion && hasValidTemplates(cachedData.program)) {
              console.log(` Using cached static program: ${programId} v${staticVersion}`);
              return cachedData.program;
            }
          } catch { /* invalid cache, continue */ }
        }

        // Cache the static version (new version or cache miss)
        console.log(` Loading static program: ${programId} v${staticVersion}`);
        try {
          localStorage.setItem(cacheKey, JSON.stringify({
            program: staticProgram,
            timestamp: Date.now(),
            version: staticVersion,
          }));
        } catch { /* storage full, proceed without caching */ }

        return staticProgram;
      }
    }

    // Non-static programs (custom/Firestore-only): use cache → Firestore
    const cached = localStorage.getItem(cacheKey);

    if (cached) {
      const cachedData: CachedProgram = JSON.parse(cached);
      const age = Date.now() - cachedData.timestamp;

      if (age < CACHE_DURATION && hasValidTemplates(cachedData.program)) {
        console.log(` Using cached program: ${programId} (age: ${Math.round(age / 1000 / 60)}min)`);
        return cachedData.program;
      } else if (!hasValidTemplates(cachedData.program)) {
        console.warn(` Cached program ${programId} has no templates, invalidating cache`);
        localStorage.removeItem(cacheKey);
      } else {
        console.log(`⏰ Cache expired for ${programId}, fetching fresh data`);
      }
    }

    // Cache miss or expired - fetch from Firestore
    console.log(` Fetching program from Firestore: ${programId}`);

    // Use unified loader for custom programs
    let program = isCustomProgram(programId) && userId
      ? await getProgramByIdUnified(userId, programId)
      : await getProgramById(programId);

    if (!program) {
      console.error(` Program not found anywhere: ${programId}`);
      return null;
    }

    // Verify Firestore program has valid templates
    if (!hasValidTemplates(program)) {
      console.warn(` Program ${programId} from Firestore has no templates!`);
      return null;
    }

    // Verify we have templates
    if (!hasValidTemplates(program)) {
      console.error(` CRITICAL: Program ${programId} has no templates even after fallback`);
      console.error(' Available data:', {
        hasTemplates: !!program.templates,
        templateCount: program.templates ? Object.keys(program.templates).length : 0,
        hasWorkouts: !!program.workouts,
        workoutCount: program.workouts?.length || 0,
      });
    }

    // Store in cache
    const cacheData: CachedProgram = {
      program,
      timestamp: Date.now(),
      version: program.metadata.version || '1.0.0',
    };

    try {
      localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      console.log(` Cached program: ${programId} (v${cacheData.version})`);
    } catch (storageError) {
      // localStorage might be full - clear old program caches
      console.warn(' localStorage full, clearing old program caches');
      clearOldProgramCaches();

      // Try again
      try {
        localStorage.setItem(cacheKey, JSON.stringify(cacheData));
      } catch {
        console.error(' Failed to cache program after cleanup');
      }
    }

    return program;
  } catch (error) {
    console.error(` Error in getCachedProgram for ${programId}:`, error);

    // Try static program as fallback
    if (hasStaticProgram(programId)) {
      console.log(` Error fallback: using static program for ${programId}`);
      return getStaticProgram(programId);
    }

    // Try to return cached version even if expired (offline fallback)
    const cacheKey = `${CACHE_PREFIX}${programId}`;
    const cached = localStorage.getItem(cacheKey);
    if (cached) {
      console.log(' Using expired cache as fallback');
      const cachedData: CachedProgram = JSON.parse(cached);
      return cachedData.program;
    }

    return null;
  }
}

/**
 * Invalidate cache for a specific program
 * Use when program is updated
 */
export function invalidateProgramCache(programId: string): void {
  const cacheKey = `${CACHE_PREFIX}${programId}`;
  localStorage.removeItem(cacheKey);
  console.log(` Invalidated cache for: ${programId}`);
}

/**
 * Clear all program caches
 */
export function clearAllProgramCaches(): void {
  const keys = Object.keys(localStorage);
  let cleared = 0;

  keys.forEach(key => {
    if (key.startsWith(CACHE_PREFIX)) {
      localStorage.removeItem(key);
      cleared++;
    }
  });

  console.log(` Cleared ${cleared} program caches`);
}

/**
 * Clear old program caches (older than 7 days)
 */
function clearOldProgramCaches(): void {
  const keys = Object.keys(localStorage);
  const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
  let cleared = 0;

  keys.forEach(key => {
    if (key.startsWith(CACHE_PREFIX)) {
      try {
        const cached = localStorage.getItem(key);
        if (cached) {
          const cachedData: CachedProgram = JSON.parse(cached);
          if (cachedData.timestamp < sevenDaysAgo) {
            localStorage.removeItem(key);
            cleared++;
          }
        }
      } catch {
        // Invalid cache entry, remove it
        localStorage.removeItem(key);
        cleared++;
      }
    }
  });

  console.log(` Cleared ${cleared} old program caches`);
}

/**
 * Get cache stats for debugging
 */
export function getProgramCacheStats(): {
  totalCached: number;
  cacheSize: number;
  programs: Array<{ id: string; age: number; version: string; hasTemplates: boolean }>;
} {
  const keys = Object.keys(localStorage);
  const programs: Array<{ id: string; age: number; version: string; hasTemplates: boolean }> = [];
  let totalSize = 0;

  keys.forEach(key => {
    if (key.startsWith(CACHE_PREFIX)) {
      const cached = localStorage.getItem(key);
      if (cached) {
        totalSize += cached.length;
        try {
          const cachedData: CachedProgram = JSON.parse(cached);
          programs.push({
            id: key.replace(CACHE_PREFIX, ''),
            age: Date.now() - cachedData.timestamp,
            version: cachedData.version,
            hasTemplates: hasValidTemplates(cachedData.program),
          });
        } catch {
          // Invalid cache entry
        }
      }
    }
  });

  return {
    totalCached: programs.length,
    cacheSize: totalSize,
    programs,
  };
}

/**
 * Force refresh a program from static data
 * Use when Firebase data is known to be bad
 */
export function forceRefreshFromStatic(programId: string): WorkoutProgram | null {
  if (!hasStaticProgram(programId)) {
    console.error(` No static program for: ${programId}`);
    return null;
  }

  // Clear cache
  invalidateProgramCache(programId);

  // Get static program
  const program = getStaticProgram(programId);
  if (!program) return null;

  // Cache it
  const cacheKey = `${CACHE_PREFIX}${programId}`;
  const cacheData: CachedProgram = {
    program,
    timestamp: Date.now(),
    version: program.metadata.version || '1.0.0',
  };

  try {
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
    console.log(` Force refreshed ${programId} from static data`);
  } catch {
    console.error(' Failed to cache static program');
  }

  return program;
}
